import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface Template {
  id: string
  name: string
  category: "initial_outreach" | "follow_up" | "objection_handling" | "closing"
  platform?: string
  content: string
  variables: {
    name: string
    description?: string
    required: boolean
    defaultValue?: string
  }[]
  performanceStats?: Record<string, any>
  isActive: boolean
  createdAt: string
  updatedAt: string
}

const CategoryBadge: React.FC<{ category: string }> = ({ category }) => {
  const colors = {
    initial_outreach: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-300",
    follow_up: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-300",
    objection_handling: "bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-300",
    closing: "bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-300",
  }

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[category as keyof typeof colors]}`}>
      {category.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
    </span>
  )
}

const TemplatesLibrary: React.FC = () => {
  const [categoryFilter, setCategoryFilter] = useState<string>("")
  const [platformFilter, setPlatformFilter] = useState<string>("")
  const [searchTerm, setSearchTerm] = useState("")
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)
  const [showTestModal, setShowTestModal] = useState(false)

  // Query templates with filters
  const {
    data: templatesData,
    isLoading,
    refetch,
  } = trpc.crm.templates.list.useQuery({
    category: categoryFilter
      ? (categoryFilter as "initial_outreach" | "follow_up" | "objection_handling" | "closing")
      : undefined,
    platform: platformFilter ? (platformFilter as "email" | "sms" | "whatsapp" | "messenger") : undefined,
    search: searchTerm || undefined,
    limit: 50,
    offset: 0,
  })

  // Create template mutation
  const createTemplateMutation = trpc.crm.templates.create.useMutation({
    onSuccess: () => {
      setShowCreateModal(false)
      void refetch()
    },
  })

  // Update template mutation
  const updateTemplateMutation = trpc.crm.templates.update.useMutation({
    onSuccess: () => {
      setShowEditModal(false)
      setEditingTemplate(null)
      void refetch()
    },
  })

  // Test template mutation
  const testTemplateMutation = trpc.crm.templates.test.useMutation()

  const handleCreateTemplate = (formData: FormData) => {
    const name = formData.get("name") as string
    const category = formData.get("category") as string
    const platform = formData.get("platform") as string
    const content = formData.get("content") as string

    if (!name || !category || !content) return

    // Validate category
    const validCategories = ["initial_outreach", "follow_up", "objection_handling", "closing"] as const
    if (!validCategories.includes(category as any)) return

    // Validate platform
    const validPlatforms = ["email", "sms", "whatsapp", "messenger"] as const
    const validatedPlatform = platform && validPlatforms.includes(platform as any) ? platform : undefined

    // Extract variables from content (simple regex for {{variable}})
    const variableMatches = content.match(/\{\{(\w+)\}\}/g) || []
    const variables = [...new Set(variableMatches.map((match) => match.slice(2, -2)))].map((name) => ({
      name,
      required: true,
      description: `Value for ${name}`,
    }))

    createTemplateMutation.mutate({
      name,
      category: category as "initial_outreach" | "follow_up" | "objection_handling" | "closing",
      platform: validatedPlatform as "email" | "sms" | "whatsapp" | "messenger" | undefined,
      content,
      variables,
    })
  }

  const handleEditTemplate = (formData: FormData) => {
    if (!editingTemplate) return

    const name = formData.get("name") as string
    const category = formData.get("category") as string
    const platform = formData.get("platform") as string
    const content = formData.get("content") as string

    if (!name || !category || !content) return

    // Validate category
    const validCategories = ["initial_outreach", "follow_up", "objection_handling", "closing"] as const
    if (!validCategories.includes(category as any)) return

    // Validate platform
    const validPlatforms = ["email", "sms", "whatsapp", "messenger"] as const
    const validatedPlatform = platform && validPlatforms.includes(platform as any) ? platform : undefined

    // Extract variables from content (simple regex for {{variable}})
    const variableMatches = content.match(/\{\{(\w+)\}\}/g) || []
    const variables = [...new Set(variableMatches.map((match) => match.slice(2, -2)))].map((name) => ({
      name,
      required: true,
      description: `Value for ${name}`,
    }))

    updateTemplateMutation.mutate({
      id: editingTemplate.id,
      data: {
        name,
        category: category as "initial_outreach" | "follow_up" | "objection_handling" | "closing",
        platform: validatedPlatform as "email" | "sms" | "whatsapp" | "messenger" | undefined,
        content,
        variables,
      },
    })
  }

  const handleTestTemplate = (template: Template, testData: Record<string, string>) => {
    testTemplateMutation.mutate({
      templateId: template.id,
      testData,
    })
  }

  return (
    <div className="h-full overflow-auto bg-foregroundx ">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold  dark:text-white">Message Templates</h1>
              <p className="mt-2  dark:text-gray-400">Create and manage reusable message templates</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <span>+</span>
              Create Template
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4 flex-wrap">
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
            />
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
          >
            <option value="">All Categories</option>
            <option value="initial_outreach">Initial Outreach</option>
            <option value="follow_up">Follow Up</option>
            <option value="objection_handling">Objection Handling</option>
            <option value="closing">Closing</option>
          </select>

          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
          >
            <option value="">All Platforms</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="messenger">Messenger</option>
          </select>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            <div className="col-span-full p-8 text-center">
              <div className="text-gray-500">Loading templates...</div>
            </div>
          ) : templatesData?.templates && templatesData.templates.length > 0 ? (
            templatesData.templates.map((template) => (
              <div
                key={template.id}
                className="bg-background  rounded-lg p-6 shadow-sm border border-border dark:border-gray-700"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-medium  dark:text-white">{template.name}</h3>
                  <CategoryBadge category={template.category} />
                </div>

                <div className="text-sm  dark:text-gray-400 mb-3">
                  {template.platform
                    ? `${template.platform.charAt(0).toUpperCase() + template.platform.slice(1)} only`
                    : "All platforms"}
                </div>

                <div className="text-sm text-primary dark:text-gray-300 mb-4 line-clamp-3">
                  {template.content.length > 150 ? template.content.substring(0, 150) + "..." : template.content}
                </div>

                {template.variables && Array.isArray(template.variables) && template.variables.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Variables:</div>
                    <div className="flex flex-wrap gap-1">
                      {template.variables.slice(0, 3).map((variable: any) => (
                        <span
                          key={variable.name}
                          className="text-xs bg-foregroundx dark:bg-gray-700 text-primary dark:text-gray-300 px-2 py-1 rounded"
                        >
                          {variable.name}
                        </span>
                      ))}
                      {template.variables.length > 3 && (
                        <span className="text-xs text-gray-500">+{template.variables.length - 3} more</span>
                      )}
                    </div>
                  </div>
                )}

                {template.performanceStats && typeof template.performanceStats === "object" && (
                  <div className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                    Performance: {(template.performanceStats as any).response_rate || "N/A"} response rate
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedTemplate({
                        ...template,
                        createdAt:
                          typeof template.createdAt === "string"
                            ? template.createdAt
                            : template.createdAt.toISOString(),
                        updatedAt:
                          typeof template.updatedAt === "string"
                            ? template.updatedAt
                            : template.updatedAt.toISOString(),
                        category: template.category as
                          | "initial_outreach"
                          | "follow_up"
                          | "objection_handling"
                          | "closing",
                        variables: Array.isArray(template.variables)
                          ? (template.variables as {
                              name: string
                              description?: string
                              required: boolean
                              defaultValue?: string
                            }[])
                          : [],
                      })
                      setShowTestModal(true)
                    }}
                    className="flex-1  hover:text-blue-800 text-sm py-2 px-3 border border-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  >
                    Test
                  </button>
                  <button
                    onClick={() => {
                      // @ts-ignore TODO: Fix template type issues with category field
                      setEditingTemplate(template)
                      setShowEditModal(true)
                    }}
                    className="flex-1  hover:text-gray-800 text-sm py-2 px-3 border border-gray-600 rounded  dark:hover:bg-gray-900/20"
                  >
                    Edit
                  </button>
                </div>

                <div className="text-xs text-gray-400 mt-3">
                  Created {new Date(template.createdAt).toLocaleDateString()}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full p-8 text-center">
              <div className="text-gray-500 mb-4">No templates found</div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Your First Template
              </button>
            </div>
          )}
        </div>

        {/* Create Template Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background  rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold  dark:text-white mb-4">Create New Template</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleCreateTemplate(new FormData(e.currentTarget))
                }}
              >
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                        Template Name
                      </label>
                      <input
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                        placeholder="Enter template name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Category</label>
                      <select
                        name="category"
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      >
                        <option value="">Select category</option>
                        <option value="initial_outreach">Initial Outreach</option>
                        <option value="follow_up">Follow Up</option>
                        <option value="objection_handling">Objection Handling</option>
                        <option value="closing">Closing</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      Platform (Optional)
                    </label>
                    <select
                      name="platform"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                    >
                      <option value="">All platforms</option>
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="messenger">Messenger</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      Template Content
                    </label>
                    <div className="text-xs text-gray-500 mb-2">Use {"{{variable_name}}"} for dynamic content</div>
                    <textarea
                      name="content"
                      rows={6}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="Hi {{name}}, I hope you're doing well..."
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2  dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createTemplateMutation.isLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createTemplateMutation.isLoading ? "Creating..." : "Create Template"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Template Modal */}
        {showEditModal && editingTemplate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background  rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold  dark:text-white mb-4">Edit Template: {editingTemplate.name}</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleEditTemplate(new FormData(e.currentTarget))
                }}
              >
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                        Template Name
                      </label>
                      <input
                        name="name"
                        type="text"
                        required
                        defaultValue={editingTemplate.name}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                        placeholder="Enter template name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Category</label>
                      <select
                        name="category"
                        required
                        defaultValue={editingTemplate.category}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      >
                        <option value="">Select category</option>
                        <option value="initial_outreach">Initial Outreach</option>
                        <option value="follow_up">Follow Up</option>
                        <option value="objection_handling">Objection Handling</option>
                        <option value="closing">Closing</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      Platform (Optional)
                    </label>
                    <select
                      name="platform"
                      defaultValue={editingTemplate.platform || ""}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                    >
                      <option value="">All Platforms</option>
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="messenger">Messenger</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      Template Content
                    </label>
                    <textarea
                      name="content"
                      required
                      rows={8}
                      defaultValue={editingTemplate.content}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white resize-none"
                      placeholder="Hi {{name}}, I hope you're doing well..."
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      Use {"{{"}
                      {"{variable}"}
                      {"}}"} syntax for dynamic values (e.g., {"{{"}
                      {"{name}"}
                      {"}}"}, {"{{"}
                      {"{company}"}
                      {"}"})
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false)
                      setEditingTemplate(null)
                    }}
                    className="px-4 py-2  dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateTemplateMutation.isLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updateTemplateMutation.isLoading ? "Updating..." : "Update Template"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Test Template Modal */}
        {showTestModal && selectedTemplate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background  rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold  dark:text-white mb-4">Test Template: {selectedTemplate.name}</h2>

              {selectedTemplate.variables &&
              Array.isArray(selectedTemplate.variables) &&
              selectedTemplate.variables.length > 0 ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    const formData = new FormData(e.currentTarget)
                    const testData: Record<string, string> = {}
                    selectedTemplate.variables.forEach((variable) => {
                      testData[variable.name] = (formData.get(variable.name) as string) || ""
                    })
                    handleTestTemplate(selectedTemplate, testData)
                  }}
                >
                  <div className="space-y-4 mb-6">
                    {(selectedTemplate.variables as any[]).map((variable: any) => (
                      <div key={variable.name}>
                        <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                          {variable.name} {variable.required && <span className="text-destructive">*</span>}
                        </label>
                        <input
                          name={variable.name}
                          type="text"
                          required={variable.required}
                          defaultValue={variable.defaultValue}
                          placeholder={variable.description || `Enter ${variable.name}`}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                        />
                      </div>
                    ))}
                  </div>

                  <button
                    type="submit"
                    disabled={testTemplateMutation.isLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 mb-4"
                  >
                    {testTemplateMutation.isLoading ? "Testing..." : "Test Template"}
                  </button>
                </form>
              ) : (
                <div className="mb-4">
                  <p className=" dark:text-gray-400 mb-4">This template has no variables to test.</p>
                  <button
                    onClick={() => handleTestTemplate(selectedTemplate, {})}
                    disabled={testTemplateMutation.isLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {testTemplateMutation.isLoading ? "Testing..." : "Test Template"}
                  </button>
                </div>
              )}

              {testTemplateMutation.data && (
                <div className="mt-6 p-4 bg-foregroundx dark:bg-gray-700 rounded-lg">
                  <h3 className="font-medium  dark:text-white mb-2">Test Result:</h3>
                  <div className="text-sm text-primary dark:text-gray-300 whitespace-pre-wrap">
                    {testTemplateMutation.data.renderedContent}
                  </div>
                  {testTemplateMutation.data.missingVariables.length > 0 && (
                    <div className="mt-2 text-sm text-destructive dark:text-red-400">
                      Missing required variables: {testTemplateMutation.data.missingVariables.join(", ")}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowTestModal(false)
                    setSelectedTemplate(null)
                    testTemplateMutation.reset()
                  }}
                  className="px-4 py-2  dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TemplatesLibrary
